import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    ShieldCheck, Lock, Gavel, Eye, Users, MessageSquare,
    CheckCircle2, ArrowRight, Shield,
} from "lucide-react";

const commitments = [
    {
        icon: ShieldCheck,
        title: "Verified Users",
        text: "We verify sellers with Stripe Connect KYC and bidders by phone to keep the community trusted.",
    },
    {
        icon: Lock,
        title: "Secure Transactions",
        text: "Advanced encryption and Stripe-powered payments protect your data and every transaction.",
    },
    {
        icon: Gavel,
        title: "Fair Auctions",
        text: "Sealed single bids and one-bid-per-user rules prevent manipulation and bidding wars.",
    },
    {
        icon: Eye,
        title: "Active Monitoring",
        text: "We continuously monitor activity to detect and prevent risk in real time.",
    },
    {
        icon: MessageSquare,
        title: "Report & Support",
        text: "Our team is here to help. Report concerns and we'll take action promptly.",
    },
];

const protections = [
    "Fraud detection & prevention",
    "User behavior monitoring",
    "Identity verification (Stripe KYC)",
    "Policy enforcement",
    "Dedicated trust & safety team",
];

// Floating icon node used in the hero shield illustration.
function OrbitNode({ icon: Icon, className, delay = 0 }) {
    return (
        <span
            className={`absolute flex h-12 w-12 items-center justify-center rounded-full border border-electric/30 bg-card/80 text-electric shadow-[0_8px_24px_rgba(7,16,34,0.55)] backdrop-blur ts-float ${className}`}
            style={{ animationDelay: `${delay}s` }}
        >
            <Icon className="h-5 w-5" />
        </span>
    );
}

function HeroShield() {
    return (
        <div className="relative mx-auto aspect-square w-full max-w-[460px]" aria-hidden data-testid="trust-hero-illustration">
            {/* ambient glow */}
            <div className="absolute left-1/2 top-1/2 -z-10 h-[70%] w-[70%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-electric/25 blur-[80px] ts-pulse-glow" />

            {/* dashed orbit rings */}
            <div className="absolute inset-[6%] rounded-full border border-dashed border-electric/15 ts-spin-slow" />
            <div className="absolute inset-[20%] rounded-full border border-dashed border-electric/10" />

            {/* orbit nodes */}
            <OrbitNode icon={Users} className="left-1/2 top-[2%] -translate-x-1/2" delay={0} />
            <OrbitNode icon={Lock} className="left-[6%] top-[30%]" delay={0.8} />
            <OrbitNode icon={Shield} className="right-[6%] top-[30%]" delay={1.4} />
            <OrbitNode icon={Gavel} className="bottom-[26%] left-[4%]" delay={2.0} />
            <OrbitNode icon={Eye} className="bottom-[26%] right-[4%]" delay={1.1} />

            {/* central shield */}
            <div className="ts-float absolute left-1/2 top-[42%] flex h-40 w-36 -translate-x-1/2 -translate-y-1/2 items-center justify-center">
                <div className="relative flex h-full w-full items-center justify-center rounded-[2rem] border border-electric/40 bg-gradient-to-b from-[hsl(212,70%,22%)] to-[hsl(221,55%,12%)] shadow-[0_0_60px_rgba(33,150,255,0.45)]">
                    <ShieldCheck className="h-20 w-20 text-electric drop-shadow-[0_0_18px_rgba(33,150,255,0.8)]" strokeWidth={1.5} />
                </div>
            </div>

            {/* pedestal */}
            <div className="absolute bottom-[10%] left-1/2 h-10 w-44 -translate-x-1/2 rounded-full border border-electric/30 bg-[radial-gradient(50%_120%_at_50%_0%,rgba(33,150,255,0.4),rgba(33,150,255,0)_70%)]" />
            <div className="absolute bottom-[8%] left-1/2 h-1.5 w-40 -translate-x-1/2 rounded-full bg-electric/50 blur-[2px]" />
        </div>
    );
}

export default function TrustSafety() {
    return (
        <div data-testid="trust-safety-page" className="bg-background">
            {/* ---------- HERO (dark) ---------- */}
            <section className="noise-overlay relative overflow-hidden border-b border-border">
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(70%_60%_at_75%_25%,rgba(33,150,255,0.16)_0%,rgba(33,150,255,0)_60%)]" />
                <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
                    <div>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-electric/30 bg-electric/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-electric" data-testid="trust-hero-eyebrow">
                            Trust &amp; Safety
                        </span>
                        <h1 className="mt-5 font-display text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
                            Building trust.
                            <br />
                            <span className="text-electric">Securing every bid.</span>
                        </h1>
                        <p className="mt-5 max-w-md text-lg leading-relaxed text-muted-foreground">
                            At OmniAuc, trust is the foundation of every auction. We're committed to a safe, transparent, and reliable experience for every buyer and seller.
                        </p>
                        <Button asChild size="lg" className="mt-8 rounded-[10px] bg-electric text-white hover:bg-electric/90" data-testid="trust-hero-cta">
                            <a href="#commitment">Our Commitment</a>
                        </Button>
                    </div>
                    <HeroShield />
                </div>
            </section>

            {/* ---------- OUR COMMITMENT (light) ---------- */}
            <section id="commitment" className="bg-[#f4f5fb] py-16 sm:py-20" data-testid="trust-commitment-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <div className="mx-auto max-w-2xl text-center">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-electric">Our Commitment</p>
                        <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                            A safe marketplace for everyone
                        </h2>
                        <p className="mt-3 text-base leading-relaxed text-slate-500">
                            We use industry best practices, smart technology, and a dedicated team to protect our community and keep auctions fair.
                        </p>
                    </div>

                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
                        {commitments.map((c) => {
                            const Icon = c.icon;
                            return (
                                <div
                                    key={c.title}
                                    data-testid="trust-commitment-card"
                                    className="group rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(15,23,42,0.10)]"
                                >
                                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-electric/10 text-electric transition-colors duration-200 group-hover:bg-electric group-hover:text-white">
                                        <Icon className="h-6 w-6" />
                                    </span>
                                    <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">{c.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-500">{c.text}</p>
                                </div>
                            );
                        })}
                    </div>

                    {/* ---------- TECHNOLOGY + PEOPLE ---------- */}
                    <div className="mt-16 grid items-center gap-10 lg:mt-20 lg:grid-cols-2" data-testid="trust-protect-section">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-electric">How we protect our community</p>
                            <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">
                                Technology + People.
                                <br />
                                Stronger together.
                            </h2>
                            <p className="mt-4 max-w-md text-base leading-relaxed text-slate-500">
                                Our smart systems work 24/7 to detect suspicious activity, while our dedicated trust &amp; safety team reviews and takes action when needed.
                            </p>
                            <ul className="mt-6 space-y-3">
                                {protections.map((p) => (
                                    <li key={p} className="flex items-center gap-2.5 text-sm font-medium text-slate-700">
                                        <CheckCircle2 className="h-5 w-5 shrink-0 text-electric" />
                                        {p}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* image card */}
                        <div className="relative overflow-hidden rounded-2xl border border-slate-900/10 shadow-[0_20px_50px_rgba(15,23,42,0.20)]">
                            <img
                                src="https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=1600&auto=format&fit=crop"
                                alt="OmniAuc trust & safety operations center"
                                loading="lazy"
                                className="h-[420px] w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[hsl(221,59%,9%)] via-[hsl(221,59%,9%)]/55 to-transparent" />
                            <div className="absolute inset-x-0 bottom-0 p-7">
                                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-electric text-white shadow-[0_0_24px_rgba(33,150,255,0.55)]">
                                    <Shield className="h-5 w-5" />
                                </span>
                                <h3 className="mt-4 font-display text-2xl font-bold text-white">
                                    Real people.
                                    <br />
                                    Real protection.
                                </h3>
                                <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-300">
                                    Our team is always working behind the scenes to keep OmniAuc a trusted place to buy and sell.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ---------- CTA BAND ---------- */}
                    <div
                        className="mt-14 flex flex-col items-start gap-6 rounded-2xl border border-slate-200/80 bg-white p-7 sm:p-9 lg:flex-row lg:items-center lg:justify-between"
                        data-testid="trust-cta-band"
                    >
                        <div className="flex items-center gap-5">
                            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-electric/10">
                                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-electric text-white shadow-[0_0_24px_rgba(33,150,255,0.45)]">
                                    <ShieldCheck className="h-6 w-6" />
                                </span>
                            </span>
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-electric">Your trust, our priority</p>
                                <h3 className="mt-1 font-display text-2xl font-bold tracking-tight text-slate-900">We're here to help</h3>
                                <p className="mt-1.5 max-w-md text-sm leading-relaxed text-slate-500">
                                    If you see something that doesn't seem right, let us know. Your report helps keep our community safe.
                                </p>
                            </div>
                        </div>
                        <Button asChild size="lg" className="shrink-0 rounded-[10px] bg-electric text-white hover:bg-electric/90" data-testid="trust-report-btn">
                            <Link to="/disputes">Report an Issue <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
}
